// =============================
// File: src/views/Layouts.jsx
// Notes (TH):
// - รวม DOM writes เป็นฟังก์ชัน setCssVars + updateManifestLinks
// - เพิ่มตัวแปร CSS ใหม่: --label-name, --bg-rank-a, --bg-rank-b
// - OFFLINE: คอมเมนต์ flow offline ออก (เก็บ state/data ชื่อเดิมไว้เพื่อไม่ให้โค้ดส่วนอื่นพัง)
// - ใช้ useRef กัน GTM initialize ซ้ำ และ revoke Blob URL กัน memory leak
// - ปรับ breakpoint เป็น 'md' ตามโค้ดล่าสุด
// - คงพฤติกรรม query params เดิมครบถ้วน
// =============================

import { useEffect, useMemo, useRef, useState } from 'react'
import TagManager from 'react-gtm-module'

// Auth
import { isToken, isUserLoggedIn } from '@core/auth/utils'

// Hooks
import useEffectOnce from '@utility/hooks/useEffectOnce'
import useParameterUrl from '@utility/hooks/useParameterUrl'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

// Routes
import { BrowserRouter as Router } from 'react-router-dom'
import ViewsRoutes from './ViewsRoutes'

// @Utility
import { setStorage, getStorage, removeStorage } from '@utility/app/storage'
import loadLiveChat from '@utility/app/loadLiveChat'

// Redux & Action
import { useDispatch, useSelector } from 'react-redux'
import { setDataCMS } from '@core/redux/actions/app/cms'
import { loadSetting } from '@core/redux/actions/app/dataSetting'
import { loadDataCampAll } from '@core/redux/actions/camp'
import { openAuthenApp, loginToken } from '@core/redux/actions/auth'

// Component
import LoadSpinner from 'views/components/LoadSpinner'
import ErrorApp from 'views/components/Error'
import ConfigSet from 'views/components/ConfigSet'
import NotificationSubscription from 'views/components/NotificationSubscription'

const STORAGE = {
    setting: 'setting',
    dataCMS: 'data_cms',
    agentContact: 'agent_contact',
    agentLogo: 'agent_logo',
    allusion: 'allusion',
    affiliate: 'affiliate',
    clickid: 'clickid',
    platform: 'platform',
    fbclid: 'fbclid',
    fbctimestamp: 'fbctimestamp',
    refer: 'refer',
    market: 'market',
    notificationStatus: 'notificationStatus'
}

const truthy = (v) => v === true || v === 'true'
const safeJSONParse = (txt) => { try { return JSON.parse(txt) } catch { return null } }

const setCssVars = (cms = {}) => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const map = {
        '--bg-navbar': 'bg-navbar',
        '--bg-primary': 'bg-primary',
        '--bg-secondary': 'bg-secondary',
        '--bg-confirm': 'bg-confirm',
        '--text-bt-confirm': 'text-bt-confirm',
        '--bt-login': 'bt-login',
        '--text-bt-login': 'text-bt-login',
        '--bt-confirm': 'bt-confirm',
        '--bg-brand': 'bg-brand',
        '--bg-label-money': 'bg-label-money',
        '--bg-camp': 'bg-camp',
        '--text-secondary': 'text-secondary',
        '--bg-card': 'bg-card',
        '--bg-select': 'bg-select',
        '--bg-floor': 'bg-floor',
        '--bg-menu-floor': 'bg-menu-floor',
        '--bg-label-text': 'bg-label-text',
        '--bt-sucess': 'bt-sucess',
        '--bt-danger': 'bt-danger',
        '--bg-warning': 'bg-warning',
        '--bg-blue': 'bg-blue',
        '--bg-black': 'bg-black',
        '--bg-white': 'bg-white',
        '--bg-text-main': 'bg-text-main',
        '--bg-play-game': 'bg-play-game',
        '--bg-popup': 'bg-popup',
        '--bg-floor-popup': 'bg-floor-popup',
        '--label-name': 'label-name',
        '--bg-rank-a': 'bg-rank-a',
        '--bg-rank-b': 'bg-rank-b',
        '--bg-gray': 'bg-gray'
    }
    Object.entries(map).forEach(([cssVar, key]) => {
        if (cms[key] != null) root.style.setProperty(cssVar, cms[key])
    })
    if (cms['bg-floor'] && document.body) {
        document.body.style.background = cms['bg-floor']
    }
}

const updateManifestLinks = (cms) => {
    if (typeof document === 'undefined') return { url: null }
    const urlDomain = `${window.location.protocol}//${window.location.host}`
    const manifest = {
        short_name: cms['manifest_short_name'],
        name: cms['manifest_name'],
        icons: [
            { src: cms?.manifest_icon_xxl || '', type: 'image/webp', sizes: '512x512' },
            { src: cms?.manifest_icon_xxl_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '512x512' },
            { src: cms?.manifest_icon_xl || '', type: 'image/webp', sizes: '196x196' },
            { src: cms?.manifest_icon_xl_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '196x196' },
            { src: cms?.manifest_icon_lg || '', type: 'image/webp', sizes: '144x144' },
            { src: cms?.manifest_icon_lg_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '144x144' },
            { src: cms?.manifest_icon_md || '', type: 'image/webp', sizes: '128x128' },
            { src: cms?.manifest_icon_md_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '128x128' },
            { src: cms?.manifest_icon_sm || '', type: 'image/webp', sizes: '96x96' },
            { src: cms?.manifest_icon_sm_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '96x96' },
            { src: cms?.manifest_icon_xs || '', type: 'image/webp', sizes: '64x64' },
            { src: cms?.manifest_icon_xs_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '64x64' }
        ],
        scope: cms['manifest_scope'],
        start_url: urlDomain,
        display: cms['manifest_display'],
        theme_color: cms['manifest_theme_color'],
        background_color: cms['manifest_background_color']
    }
    const str = JSON.stringify(manifest)
    const blob = new Blob([str], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const manifestEl = document.querySelector('#manifest-project')
    const appleEl = document.querySelector('#apple-touch-project')
    const iconRel = document.querySelector('#icon-rel')
    if (manifestEl) manifestEl.setAttribute('href', url)
    if (appleEl && cms['manifest_icon_lg']) appleEl.setAttribute('href', cms['manifest_icon_lg'])
    if (iconRel && cms['rel_icon_big']) iconRel.setAttribute('href', cms['rel_icon_big'])

    return { url }
}

const Layouts = ({ domain }) => {
    const dispatch = useDispatch()
    const dataSetting = useSelector((s) => s.reduxAppDataSetting.result)
    const reduxSpinner = useSelector((s) => s.reduxAppSpinner.data)

    const parsed = useParameterUrl()
    const { login, register, affiliate, refer, ref, market, token, clickid, fbclid, remember, fbcid, fbuuid, platform } = parsed

    const theme = useTheme()
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))

    const [dataSettingError, setDataSettingError] = useState(null)
    // const [dataSettingOffline, setDataSettingOffline] = useState(null) // OFFLINE: คง state ไว้ แต่ไม่ใช้งานจริง
    const [maintainnerStatus, setMaintainnerStatus] = useState(null)
    const [configInstallPWA, setConfigInstallPWA] = useState(null)
    const [notificationStatus, setNotificationStatus] = useState('load')

    const gtmInitedRef = useRef(false)
    const manifestUrlRef = useRef(null)

    const isTokenMemo = useMemo(() => isToken(), [])
    const isLoggedInMemo = useMemo(() => isUserLoggedIn(), [])

    const renderRoutes = () => (
        isTokenMemo && (
            <Router>
                <ViewsRoutes />
                <ConfigSet dataCMS={dataSetting?.cms} />
            </Router>
        )
    )

    const setLoadDataCms = (cms) => {
        dispatch(setDataCMS(cms))
        setStorage(STORAGE.dataCMS, JSON.stringify(cms))
        setCssVars(cms)

        if (cms['google_key'] && !gtmInitedRef.current) {
            TagManager.initialize({ gtmId: cms['google_key'] })
            gtmInitedRef.current = true
        }

        const { url } = updateManifestLinks(cms)
        if (manifestUrlRef.current) URL.revokeObjectURL(manifestUrlRef.current)
        manifestUrlRef.current = url

        if (cms?.section_notificaion_pwa === 1 || cms?.section_notificaion_pwa === '1') {
            const urlDomain = `${window.location.protocol}//${window.location.host}`
            setConfigInstallPWA({
                title: `แอพ ${cms['manifest_name']}`,
                body: `คลิกเพื่อติดตั้งแอพ ${cms['manifest_name']}`,
                text: cms['title_notificaion_pwa'],
                icon: cms['manifest_icon_lg'],
                data: { url: urlDomain }
            })
            const n = getStorage(STORAGE.notificationStatus)
            setNotificationStatus(n)
        } else {
            setNotificationStatus('load')
        }
    }

    const renderContent = () => {
        // เดิม: if (!dataSettingError && !maintainnerStatus && !dataSettingOffline)
        if (!dataSettingError && !maintainnerStatus /* && !dataSettingOffline */) return renderRoutes()
        if (maintainnerStatus) return <ErrorApp typeError='maintainer' />
        if (dataSettingError) return <ErrorApp typeError='noData' />
        // OFFLINE: ปิดการแสดงผลหน้า offline
        // if (dataSettingOffline) return <ErrorApp typeError='offline' />
        return null
    }

    const reloadSetting = (setting) => {
        try {
            if (setting?.cms != null) {
                removeStorage(STORAGE.dataCMS)
                setLoadDataCms(setting.cms)
            } else {
                const cache = getStorage(STORAGE.dataCMS)
                if (cache) {
                    const cms = safeJSONParse(cache)
                    if (cms) setLoadDataCms(cms)
                    // OFFLINE: else setDataSettingOffline(true)
                }
            }

            const st = setting?.agent?.agent_staus
            if (st === 2 || st === 0) setMaintainnerStatus(true)
            else setMaintainnerStatus(false)
        } catch {
            // OFFLINE: setDataSettingOffline(true)
        }
    }

    // เมื่อ redux dataSetting มา ให้พยายามใช้ก่อน
    useEffect(() => {
        if (dataSetting != null) {
            reloadSetting(dataSetting)
            return
        }
        // เดิม: เช็ค (dataSettingError || dataSettingOffline)
        if (dataSetting == null && dataSettingError) {
            const raw = getStorage(STORAGE.setting)
            const obj = raw ? safeJSONParse(raw) : null
            if (obj) reloadSetting(obj)
            // else setDataSettingOffline(true)
        }
    }, [dataSetting, dataSettingError /* , dataSettingOffline */])

    // query params
    useEffect(() => {
        setStorage(STORAGE.allusion, document.referrer)

        if (!isLoggedInMemo) {
            if (truthy(login)) dispatch(openAuthenApp({ tab: 'login', show: true }))
            if (truthy(register)) dispatch(openAuthenApp({ tab: 'register', show: true }))
            if (truthy(remember)) dispatch(openAuthenApp({ tab: 'remember', show: true }))
        }

        if (affiliate !== undefined) setStorage(STORAGE.affiliate, affiliate)
        if (clickid) setStorage(STORAGE.clickid, clickid)
        if (platform) setStorage(STORAGE.platform, platform)

        const fbParam = [fbclid, fbcid, fbuuid].find(Boolean)
        if (fbParam) {
            const prev = getStorage(STORAGE.fbclid)
            setStorage(STORAGE.fbclid, fbParam)
            if (prev !== fbParam) setStorage(STORAGE.fbctimestamp, Date.now())
        }

        if (refer !== undefined) setStorage(STORAGE.refer, refer)
        else if (ref !== undefined) setStorage(STORAGE.refer, ref)

        if (market !== undefined) setStorage(STORAGE.market, market)
    }, [login, register, remember, affiliate, refer, ref, market, clickid, fbclid, fbcid, fbuuid, platform, dispatch, isLoggedInMemo])

    // เรียก API setting เมื่อมี token (ตัด offline param ออก)
    useEffectOnce(() => {
        if (isTokenMemo) {
            dispatch(loadSetting({ domain }, setDataSettingError))
        }
    })

    // ตั้งค่ายิงโหลด camp เมื่อ CMS พร้อม
    useEffect(() => {
        if (dataSettingError === false) dispatch(loadDataCampAll({ type: 99 }))
    }, [dataSettingError, dispatch])

    // live chat
    useEffect(() => {
        const contacts = dataSetting?.agent_contact
        if (Array.isArray(contacts) && contacts.length) {
            const live = contacts.find((it) => it.contact_channel === 1)
            if (live?.channel_appid) loadLiveChat(live.channel_appid)
        }
    }, [dataSetting])

    // auto login by token
    useEffectOnce(() => {
        if (!isLoggedInMemo && token && token !== 'undefined') dispatch(loginToken({ token }))
    })

    // cleanup manifest blob
    useEffect(() => () => { if (manifestUrlRef.current) URL.revokeObjectURL(manifestUrlRef.current) }, [])

    return (
        <>
            {reduxSpinner?.status && <LoadSpinner textProcess={reduxSpinner.textProcess} />}
            {renderContent()}
            {notificationStatus !== 'load' &&
                isLargeScreen &&
                (notificationStatus === null || notificationStatus === undefined || notificationStatus === '') &&
                configInstallPWA && (
                    <NotificationSubscription
                        setStorage={setStorage}
                        configInstallPWA={configInstallPWA}
                        notificationStatus={notificationStatus === null || notificationStatus === undefined || notificationStatus === ''}
                    />
                )}
        </>
    )
}

export default Layouts
